/**
 * The alerts screens' pure decisions (phase 7b item 4): how a severity shows,
 * the badge's count and tone, a type's label, and the settings form's
 * dirtiness. Table-tested in `alerts.test.ts`.
 */
import { editableAlertSettings } from '@acp/api-client/alertSettings.js';
import type { AlertCountsWire, AlertSettingsWire, AlertTypeOpenWire, SeverityOpenWire, UpdateAlertSettingsRequestWire } from '@acp/api-contract/alerts.js';
import { ALERT_CATALOG, severityRank, type Severity } from '@acp/tenant-alerts/catalog.js';
import { UNKNOWN_LABEL } from './openEnum.js';

export type SeverityTone = 'offline' | 'warning' | 'secondary';

/** A severity's badge variant: critical is the `offline` red, warning the amber, info plain. */
export function severityTone(severity: SeverityOpenWire): SeverityTone {
  switch (severity) {
    // A severity from a newer cloud (D37): neutral.
    case 'unknown':
      return 'secondary';
    case 'critical':
      return 'offline';
    case 'warning':
      return 'warning';
    case 'info':
      return 'secondary';
  }
}

/** A type's label; a type from a newer cloud (D37) is `Unknown`. */
export function alertLabel(type: AlertTypeOpenWire): string {
  return type === 'unknown' ? UNKNOWN_LABEL : ALERT_CATALOG[type].label;
}

/** How severe, for sorting: a severity from a newer cloud ranks as a warning — above information, below critical. */
export function openSeverityRank(severity: SeverityOpenWire): number {
  return severityRank(severity === 'unknown' ? 'warning' : severity);
}

/** What the top bar's badge shows: how many active alerts, and the worst severity among them (null: none). */
export function badgeOf(counts: AlertCountsWire): { readonly total: number; readonly worst: Severity | null } {
  const total = counts.critical + counts.warning + counts.info;
  const worst: Severity | null = counts.critical > 0 ? 'critical' : counts.warning > 0 ? 'warning' : counts.info > 0 ? 'info' : null;
  return { total, worst };
}

/** The part of the settings the form edits (the channels have their own controls). */
export type RulesDraft = Pick<UpdateAlertSettingsRequestWire, 'cooldownMinutes' | 'rules' | 'siteOverrides'>;

/** The form's draft: what this page may send back — a type holding a severity it does not know is left out (D37). */
export function draftOf(settings: AlertSettingsWire): RulesDraft {
  return { cooldownMinutes: settings.cooldownMinutes, ...editableAlertSettings(settings) };
}

/** Whether the draft differs from what the settings say (the live-update banner's question, D35). */
export function isRulesDirty(draft: RulesDraft, base: AlertSettingsWire): boolean {
  return JSON.stringify(draft) !== JSON.stringify(draftOf(base));
}
